import { FormStep, OfferSchemaType, removeEmptyStringKeys } from 'remote/global';
import { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useUsersCurrentAccessesQuery } from 'api/queries/users';
import { differenceBy } from 'lodash-es';
import { OrderRequestPayload } from 'utils/data/data-types';
import { CreateRideOfferEnum } from 'utils/data/enums';
import { toast } from 'react-hot-toast';
import { runSequentially } from 'utils/helpers';
import { useTranslation } from 'react-i18next';
import {
  usePostOperatorsOffersEmailMutation,
  usePostOperatorsOffersMutation,
  usePostOperatorsOffersPriceMutation,
  usePostOperatorsOrdersMutation,
} from 'api/queries/operators';
import CreateRideOffersForm from './CreateRideOffersForm/CreateRideOffersForm';
import SelectRideOfferForm from './SelectRideOfferForm';
import {
  RideDetailsSchemaType,
  RideOffersSchemaType,
  RideOffersPayloadSchemaType,
  RideSupplierSchemaType,
} from './types';
import AssignRideSupplierForm from './AssignRideSupplierForm';

export type CreateRideFormResult = [
  Partial<RideDetailsSchemaType>?,
  RideOffersSchemaType?,
  RideSupplierSchemaType?
];
type CreateRideModalFormProps = {
  onClose: () => void;
  defaultValues?: CreateRideFormResult;
  isCreateOfferVariant?: boolean;
};

function CreateRideForm({
  isCreateOfferVariant,
  onClose,
  defaultValues = [],
}: CreateRideModalFormProps) {
  const { data: userInfo } = useUsersCurrentAccessesQuery();
  const [formResult, setFormResult] = useState<CreateRideFormResult>();
  const [rideDetailsValues, selectOffersValues, rideSupplierValues] = formResult ?? defaultValues;
  const [activeStep, setActiveStep] = useState(0);
  const { operatorId } = userInfo ?? {};
  const { t } = useTranslation();
  const { defaultOperatorOffers, operatorOfferUuids } = selectOffersValues ?? {};
  const postOperatorsOffersMutation = usePostOperatorsOffersMutation();
  const postOperatorsOrdersMutation = usePostOperatorsOrdersMutation();
  const postOperatorsOffersEmailMutation = usePostOperatorsOffersEmailMutation();
  const postOperatorsOffersPriceMutation = usePostOperatorsOffersPriceMutation();

  const steps: FormStep[] = [
    { label: 'Create Offers', description: '' },
    { label: 'Select Offer', description: '' },
    { label: 'Create Ride', description: '' },
  ];

  const setFormResultForStep = (step: number, data: any) => {
    setFormResult((prevState) => {
      const newState = prevState ?? [];
      newState[step] = data;
      return newState;
    });
  };

  const handleGoForward = (data: any) => {
    const nextStepIndex = activeStep + 1;
    // If is not the last step
    if (nextStepIndex < steps.length) {
      setFormResultForStep(activeStep, data);
      setActiveStep(nextStepIndex);
    }
  };

  const handleGoBack = (data: any) => {
    setFormResultForStep(activeStep, data);
    setActiveStep(activeStep - 1);
  };

  const handleValidStep = async (data: FieldValues) => {
    const nextStepIndex = activeStep + 1;
    switch (nextStepIndex) {
      case 1: {
        // When attempting 0 > 1
        const payload: RideOffersPayloadSchemaType = data as RideOffersPayloadSchemaType;

        try {
          const response = await postOperatorsOffersMutation.mutateAsync(
            removeEmptyStringKeys(payload)
          );
          if (response != null) {
            const resultOffers = response.trips.flatMap(({ operatorOffers }) => operatorOffers);
            const offerUuids = resultOffers.flatMap(({ uuid }) => uuid);
            if (offerUuids.length > 0) {
              // Sets the operator offers for the incoming step
              setFormResultForStep(nextStepIndex, {
                ...response,
                defaultOperatorOffers: resultOffers,
                operatorOfferUuids: [resultOffers[0].uuid], // Assign the fist offer as default
                // Reset the selected offer because offers are always generated with new uuids
                selectedOperatorOfferUuid: '',
              });
              // Sets the data for the current step and increases step count
              handleGoForward(data);
            } else {
              toast.error(t('Toast.noOffersFound'));
            }
          }
        } catch (e) {
          // TODO: Sentry error
        }
        break;
      }
      case 2: {
        // When attempting 1 > 2
        const payload = data as RideSupplierSchemaType;

        let priceUpdatedOffers: any[] = [];
        if (payload.rideOfferType === CreateRideOfferEnum.CREATE_ORDER) {
          const defaultOffer = defaultOperatorOffers?.find(
            ({ uuid }) => uuid === payload.selectedOperatorOfferUuid
          );
          const selectedOffer = payload.trips
            .flatMap(({ operatorOffers }) => operatorOffers)
            .find(({ uuid }) => uuid === payload.selectedOperatorOfferUuid)!;
          priceUpdatedOffers = differenceBy(
            [selectedOffer],
            [defaultOffer],
            'operatorPriceExclVat'
          );
        } else {
          // When we are creating an order, there can only be one offer that can update
          const newOffers = payload.trips.flatMap(({ operatorOffers }) => operatorOffers);
          priceUpdatedOffers = differenceBy(
            newOffers,
            defaultOperatorOffers ?? [],
            'operatorPriceExclVat'
          );
        }

        // We can improve this by taking into account 'isDirty' of the price field of the offer so that we only make the request when it is neccessary
        // Currently we make this request all the time - whenever the user goes from 1 > 2
        if (priceUpdatedOffers.length > 0) {
          try {
            let returnData: OfferSchemaType;
            const promises = priceUpdatedOffers.map(
              ({ operatorPriceExclVat, uuid }) =>
                () =>
                  postOperatorsOffersPriceMutation
                    .mutateAsync({
                      refNumber: payload.refNumber,
                      newPriceExclVat: operatorPriceExclVat!,
                      operatorOfferUuid: uuid,
                    })
                    .then((d) => {
                      returnData = d;
                    })
            );

            // Make sure to include new price in data
            await runSequentially(promises).then(() => {
              handleGoForward({ ...data, ...returnData });
            });
          } catch (err) {
            Promise.reject(err);
          }
        } else handleGoForward(data);
        break;
      }
      case 3: {
        // When attempting 3 > 4 (last step)
        const payload = data as RideSupplierSchemaType;

        if (payload.rideOfferType === CreateRideOfferEnum.CREATE_ORDER) {
          try {
            const orderPayload: OrderRequestPayload = removeEmptyStringKeys({
              clientContactEmail: payload.clientContactEmail,
              clientId: payload.clientId as number,
              operatorId,
              operatorOfferUuid: payload.selectedOperatorOfferUuid!,
              returnScheduleItem: true,
            });
            await postOperatorsOrdersMutation.mutateAsync(orderPayload);
            toast.success(t('Toast.orderCreated'));
            onClose();
          } catch (err) {
            Promise.reject(err);
          }
        } else {
          try {
            await postOperatorsOffersEmailMutation.mutateAsync({
              refNumber: payload.refNumber,
              clientEmail: payload.clientContactEmail,
              operatorOfferUuids: payload.operatorOfferUuids,
              reserveVehicle: false,
              validUntil: payload.validUntil!,
              lang: payload.lang,
            });
            toast.success(t('Toast.emailSent'));
            onClose();
          } catch (err) {
            Promise.reject(err);
          }
        }
        break;
      }
      default: {
        handleGoForward(data);
        break;
      }
    }
    return null;
  };

  return (
    <>
      {activeStep === 0 && (
        <CreateRideOffersForm
          defaultValues={{
            ...rideDetailsValues!,
            // Assigns the last step selections as the primary choice
            clientId: rideSupplierValues?.clientId ?? rideDetailsValues?.clientId ?? '',
          }}
          onValid={handleValidStep}
        />
      )}
      {activeStep === 1 && (
        <SelectRideOfferForm
          isCreateOfferVariant={isCreateOfferVariant}
          defaultValues={selectOffersValues}
          onValid={handleValidStep}
          onBack={handleGoBack}
        />
      )}
      {activeStep === 2 && (
        <AssignRideSupplierForm
          isCreateOfferVariant={isCreateOfferVariant}
          defaultValues={{
            ...rideSupplierValues!,
            ...selectOffersValues,
            clientId: rideSupplierValues?.clientId ?? rideDetailsValues?.clientId ?? '',
            clientContactEmail: rideSupplierValues?.clientContactEmail ?? '',
            operatorOfferUuids: operatorOfferUuids ?? [], // Will forward the selected offer uuid to this step
            // Assign default offer uuid when only one remains
            ...(operatorOfferUuids?.length === 1 && {
              selectedOperatorOfferUuid: operatorOfferUuids[0],
            }),
          }}
          onValid={handleValidStep}
          onBack={handleGoBack}
        />
      )}
    </>
  );
}

export default CreateRideForm;
